import React, { createContext, useContext, useState, useCallback } from 'react';

const LoaderContext = createContext(null);

/**
 * GlobalLoader — spinner global + protection double-clic
 * 
 * Usage dans n'importe quel composant :
 *   const { withLoader, loading } = useLoader();
 *   await withLoader(async () => { ... });
 * 
 * Ou pour désactiver un bouton :
 *   <button disabled={loading}>...</button>
 */
export const LoaderProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const withLoader = useCallback(async (fn, msg = '') => {
    setLoading(true);
    setMessage(msg);
    try {
      return await fn();
    } finally {
      setLoading(false);
      setMessage('');
    }
  }, []);

  return (
    <LoaderContext.Provider value={{ loading, withLoader }}>
      {children}
      {loading && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(3px)',
          animation: 'loaderFadeIn 0.15s ease'
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: '28px 36px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
            boxShadow: '0 24px 60px rgba(0,0,0,0.2)',
            minWidth: 160
          }}>
            {/* Spinner */}
            <div style={{
              width: 44, height: 44, borderRadius: '50%',
              border: '3px solid #e2e8f0',
              borderTopColor: '#002FA7',
              animation: 'loaderSpin 0.7s linear infinite'
            }} />
            {message && (
              <p style={{
                fontFamily: "'Rajdhani',sans-serif",
                fontSize: 14, color: '#64748b',
                fontWeight: 600, margin: 0, textAlign: 'center'
              }}>
                {message}
              </p>
            )}
          </div>
        </div>
      )}
      <style>{`
        @keyframes loaderSpin {
          to { transform: rotate(360deg); }
        }
        @keyframes loaderFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </LoaderContext.Provider>
  );
};

export const useLoader = () => {
  const ctx = useContext(LoaderContext);
  if (!ctx) throw new Error('useLoader must be used within LoaderProvider');
  return ctx;
};

export default LoaderProvider;
